import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { APP_STORE_URL, GOOGLE_PLAY_URL, getIsAppPublished, getIsAndroidPublished, fetchAppPublishedStatus } from '../config/appStore';
import { detectPlatform, Platform } from '../lib/platform';
import { useTheme } from '../context/ThemeContext';
import FeatureIcon from '../components/FeatureIcon';
import ComingSoonToast from '../components/ComingSoonToast';
import UtilityPageFrame from '../components/utility/UtilityPageFrame';
import { useState } from 'react';

// Upstream verify-email redirect statuses: success (plus the 'verified' alias
// kept for historical links) versus any failure/missing status.
const SUCCESS_STATUSES = new Set(['success', 'verified']);

const EmailVerified: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { dsColors } = useTheme();
  const [platform, setPlatform] = useState<Platform>('other');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const status = searchParams.get('status') || 'error';
  // React escapes this when rendered; never interpolated into HTML.
  const customMessage = searchParams.get('message');
  const isSuccess = SUCCESS_STATUSES.has(status);

  useEffect(() => {
    setPlatform(detectPlatform());
    fetchAppPublishedStatus().catch(() => {});
  }, []);

  useEffect(() => {
    document.title = isSuccess
      ? 'Email Link Processed - RidePulse'
      : 'Verification Link Problem - RidePulse';
  }, [isSuccess]);

  const showAppStoreLink = platform === 'ios' || platform === 'other';
  const showPlayStoreLink = platform === 'android' || platform === 'other';

  const storeClick = (live: () => boolean) => (e: React.MouseEvent) => {
    if (!live()) { e.preventDefault(); setShowComingSoon(true); }
  };

  return (
    <UtilityPageFrame title={isSuccess ? 'Email link processed' : 'Verification link problem'}>
      <div className="utility-card" style={{ textAlign: 'center' }}>
        <div className="mb-4" aria-hidden="true">
          <span
            className="inline-flex w-14 h-14 rounded-full items-center justify-center"
            style={{
              backgroundColor: `${isSuccess ? dsColors.successStrong : dsColors.error}1f`,
              color: isSuccess ? dsColors.successStrong : dsColors.error,
            }}
          >
            <FeatureIcon name={isSuccess ? 'checkin' : 'alert'} size={28} />
          </span>
        </div>

        {/* This page only displays the upstream result — it does not mark the
            account verified or grant any action. */}
        <p className="utility-muted" role="status" aria-live="polite" style={{ marginBottom: '1.5rem' }}>
          {isSuccess
            ? 'The email link was processed. Return to the RidePulse app to check your account status.'
            : customMessage ||
              'This verification link is expired or invalid. Request a new verification link from the RidePulse app.'}
        </p>

        <button
          type="button"
          onClick={() => { window.location.href = 'ridepulse://'; }}
          className="utility-button"
        >
          Open the RidePulse app
        </button>

        <div style={{ marginTop: '1rem' }}>
          {showAppStoreLink && (
            <a
              href={APP_STORE_URL}
              className="utility-button utility-button--quiet"
              style={{ textAlign: 'center', textDecoration: 'none', marginBottom: '0.5rem' }}
              onClick={storeClick(getIsAppPublished)}
            >
              App Store
            </a>
          )}
          {showPlayStoreLink && (
            <a
              href={GOOGLE_PLAY_URL}
              className="utility-button utility-button--quiet"
              style={{ textAlign: 'center', textDecoration: 'none' }}
              onClick={storeClick(getIsAndroidPublished)}
            >
              Google Play
            </a>
          )}
        </div>
      </div>
      {showComingSoon && <ComingSoonToast onClose={() => setShowComingSoon(false)} />}
    </UtilityPageFrame>
  );
};

export default EmailVerified;
